/**
 * Client identity — the two knobs tokens.css was built around.
 *
 * `--gov-primary` and `--gov-gold` are described there as "two knobs [that]
 * rebrand the whole app", and every accent, rail, focus ring and chip derives
 * from them. This module is what turns that promise into a setting: it writes
 * the pair onto the document root, where it overrides the token defaults for
 * every component at once. No stylesheet knows this feature exists.
 *
 * Persistence belongs to the tenant Settings table. This module holds only the
 * current in-memory pair so previews repaint instantly without turning one
 * browser's localStorage into an accidental tenant configuration source.
 */

import { contrastRatio, hexToHsl, hslToHex, mixHex, normalizeHex } from '../components/colorUtils'
import { getTheme, subscribe } from '../lib/theme'

/** The platform identity, from tokens.css. An empty value means "use these". */
export const DEFAULT_MAIN = '#0e7a63'
export const DEFAULT_ACTIVE = '#c9a24b'

let currentBrand = { main: '', active: '' }

/**
 * The rail is painted IN the main colour.
 *
 * Not a tint of it and not a darkened version of it: `base` is the client's own
 * hex, unchanged, whenever it can be. Two earlier passes derived the rail
 * instead — first by mixing the hue 26% into near-black, then by keeping the hue
 * and forcing it to a fixed dark lightness — and both produced a rail that was
 * *related* to the identity rather than made of it.
 *
 * The offsets below are lightness deltas around that colour, not absolute
 * values, so the gradient is the main colour lit slightly at the top and settled
 * slightly at the foot. Eight points across a full-height column reads as one
 * surface lit from above, not as a two-tone banner.
 *
 * `well` and `rim` are the same ladder used for the rail's STATES — the open
 * section's panel, its rim, the wash behind the page you are on. They are steps
 * in lightness rather than an alpha of the accent because how far an accent
 * lifts a surface depends entirely on how light that accent happens to be: the
 * same wash looked right on the emerald and shouted on a blue rebrand.
 *
 * `well` goes DOWN. An open section used to be a panel raised out of the rail,
 * which worked while the rail was a near-black derivation — but now that it is
 * the brand colour itself, lifting it further put the rail's smallest text on
 * its lightest surface. Recessed, the panel separates just as clearly (1.29:1
 * against the rail, versus 1.27 raised) and the links inside it become the most
 * readable text in the column instead of the least.
 */
const RAIL_OFFSET = { top: 2.5, base: 0, bottom: -5.5, well: -4.5, rim: 3.5 }

/**
 * The floor the rail's LIGHTEST stop has to clear against white.
 *
 * This is the one thing allowed to move a client's colour, and only downward.
 * A rail is a surface for white text — labels, section names, the whole menu —
 * so a brand that is too light to carry them is deepened, hue and saturation
 * untouched, by the smallest step that gets there. Most real brand colours are
 * already dark enough and come through as their own hex.
 *
 * 5.5 rather than AA's 4.5: the rail's secondary ink (sub-links, chevrons) is
 * white at reduced alpha, and it needs the headroom above the floor that solid
 * white does not.
 */
const RAIL_MIN_CONTRAST = 5.5

/** A washed-out brand would give a grey rail; a neon one, a vibrating rail. */
const RAIL_SATURATION = { min: 32, max: 78 }

/**
 * The rail in the dark theme.
 *
 * In light mode the rail is the dark object on a pale page, and being the most
 * saturated surface in the room is what makes it read as one column. Invert the
 * page and that same column becomes the BRIGHTEST thing on screen — a lit
 * emerald bar beside charcoal panels, pulling every glance to the menu instead
 * of to the work.
 *
 * So the ladder is run again over a ceiling: same hue, a calmer saturation, and
 * a lightness capped just above the app field. The rail is still the most
 * saturated surface and still reads as one object; it stops shouting. A
 * client's own hex goes through the same ceiling, so a rebrand behaves the way
 * the platform identity does.
 */
const RAIL_DARK = { maxLightness: 11.5, saturation: { min: 26, max: 58 } }

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

/**
 * The stops the rail is built from, or null when `main` is not a colour — in
 * which case the tokens.css defaults (this same ladder, run on the platform
 * emerald) stay in force.
 */
export function railStops(main, theme = getTheme()) {
  const hsl = hexToHsl(main)
  if (!hsl) return null
  const dark = theme === 'dark'
  const band = dark ? RAIL_DARK.saturation : RAIL_SATURATION
  const s = clamp(hsl.s, band.min, band.max)
  const at = (l) => hslToHex({ h: hsl.h, s, l: clamp(l, 0, 100) })

  if (dark) {
    /* No contrast walk here: the ceiling already puts every stop far below
       white, and the ladder's own offsets keep the states apart. */
    const l = Math.min(hsl.l, RAIL_DARK.maxLightness)
    return {
      top: at(l + RAIL_OFFSET.top),
      base: at(l),
      bottom: at(l + RAIL_OFFSET.bottom / 2),
      well: at(l + RAIL_OFFSET.well / 2),
      rim: at(l + RAIL_OFFSET.rim),
    }
  }

  /* Step the whole ladder down together until its lightest stop carries white.
     Half a point at a time, so a colour that needs adjusting loses as little of
     itself as possible — and a colour that does not is never touched. */
  let l = hsl.l
  while (l > 6 && contrastRatio(at(l + RAIL_OFFSET.top), '#ffffff') < RAIL_MIN_CONTRAST) {
    l -= 0.5
  }

  return {
    top: at(l + RAIL_OFFSET.top),
    base: at(l),
    bottom: at(l + RAIL_OFFSET.bottom),
    well: at(l + RAIL_OFFSET.well),
    rim: at(l + RAIL_OFFSET.rim),
  }
}

/** The rail's representative colour — what a contrast check should measure against. */
export const railColor = (main) => railStops(main)?.base ?? ''

/**
 * The gold as the rail actually paints it — Sidebar.css's `--sb-gold-bright`,
 * which lightens it so it can carry on a dark surface.
 *
 * A contrast check has to measure what is drawn. Checking the raw picked gold
 * instead reported the mark 0.7 points darker than it renders, which on the
 * lighter rail was the difference between "weak" and passing — a warning about
 * a colour that appears nowhere in the product.
 */
export const railMark = (active) => mixHex(active, '#ffffff', 0.24)

/** The variables a brand pair owns. */
function variables({ main, active }) {
  const rail = railStops(main)
  return {
    '--gov-primary': main,
    '--sidebar-accent': main,
    '--sidebar-bg': rail?.base,
    '--sidebar-bg-top': rail?.top,
    '--sidebar-bg-bottom': rail?.bottom,
    '--sidebar-well': rail?.well,
    '--sidebar-rim': rail?.rim,
    '--gov-gold': active,
  }
}

/** Writes the pair onto :root. A falsy value REMOVES the override. */
export function applyBranding(brand) {
  const root = document.documentElement
  for (const [name, value] of Object.entries(variables(brand ?? {}))) {
    if (value) root.style.setProperty(name, value)
    else root.style.removeProperty(name)
  }
}

export function readBranding() {
  return { ...currentBrand }
}

export function saveBranding(brand) {
  currentBrand = {
    main: normalizeHex(brand?.main),
    active: normalizeHex(brand?.active),
  }
  applyBranding(currentBrand)
}

/** Converts the login/session settings dictionary into the UI identity pair. */
export function brandFromSettings(settings) {
  return {
    main: normalizeHex(settings?.BrandPrimaryColor),
    active: normalizeHex(settings?.BrandActiveColor),
  }
}

/** Called before first paint; the server pair is applied as soon as login/session loads. */
export function bootBranding() {
  applyBranding(currentBrand)
  /* The rail is derived from the theme, and applyBranding writes it as an INLINE
     style on :root — which beats the stylesheet. So a stored brand has to be run
     through the ladder again whenever the theme changes, or the light rail would
     survive into dark. With no brand stored nothing is written and tokens.css
     holds both versions on its own. */
  subscribe(() => applyBranding(currentBrand))
}
